import { useState } from 'react';
import { DialogActions, DialogContent, Divider, Typography } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { Modal } from '../../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../../ui/components/pure/text-button/text-button.component';
import { Dropdown, DropdownOption } from '../../../../ui/components/pure/dropdown/dropdown.component';
import { useQueryListProjects } from '../../../../core/queries/projects.queries';
import { useMutationSetAccessedViaProject } from '../../../lockers/hubs.queries';

export interface HubAccessedViaModalProps {
  hubId: string;
  hubName: string;
  managedByProjectId: string;
  onDone: () => void;
}

export function HubAccessedViaModal({ hubId, hubName, managedByProjectId, onDone }: HubAccessedViaModalProps) {
  const [selectedProjectId, setSelectedProjectId] = useState<string>(managedByProjectId);
  const allProjects = useQueryListProjects();
  const accessedViaMutation = useMutationSetAccessedViaProject();

  const projectMenuItems = (allProjects.data ?? [])
    .slice()
    .sort((a, b) => {
      // Sort so the project that relates to this hub appears at the top
      if (a.id === managedByProjectId) return -1;
      if (b.id === managedByProjectId) return 1;
      return 0;
    })
    .map((p) => ({ label: p.name, value: p.id } as DropdownOption<string>));

  const onLink = async () => {
    if (!selectedProjectId) return;
    await accessedViaMutation.mutateAsync({ hubID: hubId, projectID: selectedProjectId });
    onDone();
  };

  return (
    <Modal title="Where is it located?" open={true} divider={true} maxWidth="xs">
      <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
        <Typography variant="body1" style={{ marginBottom: '16px' }}>
          Is the hub accessed via a building?
        </Typography>
        {accessedViaMutation.error && (
          <p>
            <Alert variant="outlined" severity="error">
              <AlertTitle>Something went wrong</AlertTitle>
              Could not link the hub. Please try again.
            </Alert>
          </p>
        )}
        <Dropdown<string>
          dropdownVariant="floating"
          placeholder={`${hubName} is accessed via`}
          menuItems={projectMenuItems}
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value as string)}
          textFieldDesktopWidth="100%"
          fullWidth
        />
      </DialogContent>
      <Divider />
      <DialogActions style={{ padding: '16px 24px' }}>
        <TextButton color="secondary" variant="outlined" onClick={onDone}>
          No, it's outside
        </TextButton>
        <TextButton
          color="primary"
          variant="outlined"
          disabled={!selectedProjectId || accessedViaMutation.isLoading}
          isLoading={accessedViaMutation.isLoading}
          onClick={onLink}
        >
          Yes, Link
        </TextButton>
      </DialogActions>
    </Modal>
  );
}
